import { Component, HostListener, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  MiniMenuService,
  emptyPromoCrops,
  MINI_MENU_LANDING_FRAMES,
  MiniMenuPromoCrops
} from 'src/app/services/mini-menu.service';
import { DownscaleResult } from 'src/app/common/image-downscale';

/** What the modal hands back: the crops, plus a new file if the image was replaced. */
export interface MiniMenuLandingCropResult {
  crops: MiniMenuPromoCrops;
  replacementFile: File | null;
  removed: boolean;
  imageDimensions?: { width: number; height: number };
  imageOperationId?: string;
}

/**
 * Landing background crop step, following HUNGERY's ImageConfigurationComponent verbatim: title
 * and close, a fixed-height body sized from the viewport with the frame tabs pushed to its
 * bottom, and one centred half-width primary action in the footer.
 *
 * The MiniMenu model specifies ONE landing image carrying three device crops
 * (MENU_IMAGE_FRAME_RATIOS.landing), so this is a single upload followed by three crop frames —
 * not three image slots.
 */
@Component({
  selector: 'app-mini-menu-landing-crop-modal',
  templateUrl: './mini-menu-landing-crop-modal.component.html',
  styleUrls: ['./mini-menu-landing-crop-modal.component.scss']
})
export class MiniMenuLandingCropModalComponent implements OnInit {
  businessPartnerId = 0;
  imageDimensions?: { width: number; height: number };
  imageOperationId?: string;
  // Set by the opener before the modal renders.
  imageSource: string | null = null;
  crops: MiniMenuPromoCrops = emptyPromoCrops();

  readonly frames = MINI_MENU_LANDING_FRAMES;
  working: MiniMenuPromoCrops = emptyPromoCrops();
  /** Canon: 60% of the window height, remeasured on resize. */
  stageHeight = 0;

  /** True while a replacement file is being downscaled. */
  isCompressing = false;
  isCropReady = false;
  imageLoadFailed = false;

  private replacementFile: File | null = null;

  constructor(private activeModal: NgbActiveModal, private toastr: ToastrService, private miniMenuService: MiniMenuService) {}

  ngOnInit(): void {
    this.working = {
      mobile: this.crops?.mobile || null,
      tablet: this.crops?.tablet || null,
      tabletPortrait: this.crops?.tabletPortrait || null
    };
    this.measure();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.measure();
  }

  onCropsChange(crops: MiniMenuPromoCrops): void {
    this.working = crops;
  }

  onCropReady(ready: boolean): void {
    this.isCropReady = ready;
    if (ready) this.imageLoadFailed = false;
  }

  /** Swapping the image restarts the crops — the old rects mean nothing on a new photo. */
  async onReplace(event: Event): Promise<void> {
    if (this.isCompressing) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    this.isCompressing = true;
    let prepared: DownscaleResult;
    try {
      prepared = await this.miniMenuService.prepareMediaFile(file, this.businessPartnerId, 1);
    } catch (error: any) {
      this.isCompressing = false;
      this.toastr.error(error?.error?.message || error?.message || 'The photo could not be prepared.');
      input.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      this.imageDimensions = { width: prepared.width, height: prepared.height };
      this.imageOperationId = crypto.randomUUID(); this.imageLoadFailed = false;
      this.replacementFile = prepared.file;
      this.working = emptyPromoCrops();
      this.isCropReady = false;
      this.imageSource = String(reader.result || '');
      this.isCompressing = false;
    };
    reader.onerror = () => {
      this.isCompressing = false;
      this.toastr.error('The selected image could not be prepared.');
    };
    reader.readAsDataURL(prepared.file);
  }

  remove(): void {
    this.activeModal.close({ crops: emptyPromoCrops(), replacementFile: null, removed: true });
  }

  confirm(): void {
    if (this.isCompressing || this.imageLoadFailed || !this.isCropReady) {
      return;
    }
    this.activeModal.close({
      crops: this.working,
      replacementFile: this.replacementFile,
      imageDimensions: this.imageDimensions,
      imageOperationId: this.imageOperationId,
      removed: false
    });
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }

  private measure(): void {
    this.stageHeight = Math.round(window.innerHeight * 0.6);
  }
}
